import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";

import Chat from "./chat";
import Loader from "./components/loader";
import Questions from "./components/questions";
import UserModal from "./components/userModal";
import Header from "./layout/header";
import SideBar from "./layout/sidebar";
import Auth from "./scenes/auth";
import OAuthAuthorize from "./scenes/auth/oauth-authorize";
import ConnectedApps from "./scenes/account/connected-apps";

// other apps
import GitheoApp from "./apps/githeo";
import RfpApp from "./apps/rfp";
import SelecoleApp from "./apps/selecole";
import CourseView from "./apps/selecole/scenes/course/view";
import LearnWorkshop from "./apps/selecole/scenes/learn/workshop";

// accounting app (main/default)
import Account from "./scenes/account";
import Activity from "./scenes/activity";
import Alert from "./scenes/alert";
import Assets from "./scenes/assets";
import Bank from "./scenes/bank";
import BankDuplicates from "./scenes/bank/duplicates";
import Birthdays from "./scenes/birthdays";
import Brain from "./scenes/brain";
import BudgetDescription from "./scenes/budget_description";
import BudgetSignature from "./scenes/budget_signature";
import Export from "./scenes/export";
import Sales from "./scenes/sales";
import Admin from "./scenes/admin";
import CalendarDaysOff from "./scenes/calendar_days_off";
import Cashout from "./scenes/cashout";
import ClientView from "./scenes/client_view";
import Contact from "./scenes/contact";
import LegalDocument from "./scenes/legal_document";
import Device from "./scenes/device";
import Home from "./scenes/home";
import Invoice from "./scenes/invoice";
import PublicLean from "./scenes/learn_public";
import Malt from "./scenes/malt";
import Meetings from "./scenes/meetings";
import MeetingsAi from "./scenes/meetings_ai";
import HubspotDeals from "./scenes/hubspot_deals";
import Management from "./scenes/management";
import Performance from "./scenes/management/performance";
import Organisation from "./scenes/organisation";
import BusinessUnit from "./scenes/business_unit";
import Payment from "./scenes/payment";
import People from "./scenes/people";

import Perks from "./scenes/perks";

import Project from "./scenes/project";
import Pnl from "./scenes/pnl";
import PnlContracts from "./scenes/pnl_contracts";
import Report from "./scenes/report";
import Piloting from "./scenes/piloting";
import Tace from "./scenes/tace";
import TermsPrivacy from "./scenes/terms_privacy";
import TIFO from "./scenes/tifo";
import Documentation from "./scenes/documentation";
import Resources from "./scenes/resources";

import Companion from "./scenes/companion";

import { setApp, setBusinessUnit, setOrganisation, setUser } from "./redux/auth/actions";
import api from "./services/api";
import useStore from "./services/store";
import { getAnalyticsId, initializeAnalytics, trackPageView } from "./utils/analytics";

// Composant pour gérer Google Analytics
function AnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    const trackingId = getAnalyticsId(location.pathname);
    initializeAnalytics(trackingId);
    trackPageView(location.pathname, trackingId);
  }, [location.pathname]);

  return null;
}


console.log("checling referrer", document?.referrer);
const referrer = document?.referrer;
if (referrer) {
  try {
    const refererURL = new URL(referrer);
    console.log("Referring domain:", refererURL.hostname);
  } catch (e) {
    console.log("Invalid referrer URL:", referrer);
  }
}

export default function App() {
  return (
    <BrowserRouter>
      <AnalyticsTracker />
      <Questions />
      <UserModal />
      <Chat>
        <Routes>
          <Route path="/auth/*" element={<Auth />} />
          <Route path="/oauth/authorize" element={<OAuthAuthorize />} />

          <Route element={<OpenLayout />}>
            <Route path="/budget-signature/:id" element={<BudgetSignature />} />
            <Route path="/budget-description/:id" element={<BudgetDescription />} />
            <Route path="/client-view/:id" element={<ClientView />} />
            <Route path="/contact/*" element={<Contact />} />
            <Route path="/terms-privacy" element={<TermsPrivacy />} />
            <Route path="/learn_public/*" element={<PublicLean />} />
            <Route path="/companion" element={<Companion />} />
            <Route path="/tifo" element={<TIFO />} />
          </Route>

          <Route element={<ProtectedLayout />}>
            <Route element={<RoleProtectedRoute allowedRoles={["super_admin", "admin", "user"]} element={Home} />} path="/" />
            <Route element={<RoleProtectedRoute allowedRoles={["super_admin", "admin", "user", "client", "applicant"]} element={ConnectedApps} />} path="/account/connected-apps" />
            <Route element={<RoleProtectedRoute allowedRoles={["super_admin", "admin", "user", "client", "applicant"]} element={Account} />} path="/account/*" />
            <Route element={<RoleProtectedRoute allowedRoles={["super_admin", "admin", "user", "client"]} element={Perks} />} path="/perks/*" />
            <Route element={<RoleProtectedRoute allowedRoles={["super_admin", "admin", "user", "client"]} element={Project} />} path="/project/*" />
            <Route element={<RoleProtectedRoute allowedRoles={["super_admin", "admin"]} element={Brain} />} path="/brain/*" />
            <Route element={<RoleProtectedRoute allowedRoles={["super_admin", "admin", "user"]} element={Alert} />} path="/alert/*" />
            <Route element={<RoleProtectedRoute allowedRoles={["super_admin", "admin", "user"]} element={Activity} />} path="/activity/*" />
            {/* Data/Missions/Meetings routes removed */}
            <Route element={<RoleProtectedRoute allowedRoles={["super_admin", "admin", "user"]} element={Meetings} F />} path="/meetings/*" />
            <Route element={<RoleProtectedRoute allowedRoles={["super_admin", "admin", "user"]} element={MeetingsAi} F />} path="/meeting_ai/*" />
            <Route element={<RoleProtectedRoute allowedRoles={["super_admin", "admin", "user"]} element={HubspotDeals} />} path="/hubspot_deals/*" />
            <Route element={<RoleProtectedRoute allowedRoles={["super_admin", "admin", "user"]} element={Payment} />} path="/payment/*" />
            <Route element={<RoleProtectedRoute allowedRoles={["super_admin", "admin", "user"]} element={Birthdays} />} path="/birthdays/*" />
            <Route element={<RoleProtectedRoute allowedRoles={["super_admin", "admin", "user"]} element={CalendarDaysOff} />} path="/days-off/*" />
            <Route element={<RoleProtectedRoute allowedRoles={["super_admin", "admin", "user"]} element={Assets} />} path="/assets/*" />
            <Route element={<RoleProtectedRoute allowedRoles={["super_admin", "admin", "user"]} element={Report} />} path="/report/*" />
            <Route element={<RoleProtectedRoute allowedRoles={["super_admin", "admin"]} element={Pnl} />} path="/pnl/*" />
            <Route element={<RoleProtectedRoute allowedRoles={["super_admin", "admin"]} element={PnlContracts} />} path="/pnl-contracts/*" />
            <Route element={<RoleProtectedRoute allowedRoles={["super_admin", "admin", "user"]} element={Sales} />} path="/sales/*" />
            <Route element={<RoleProtectedRoute allowedRoles={["super_admin", "admin"]} element={Admin} />} path="/admin/*" />
            <Route element={<RoleProtectedRoute allowedRoles={["super_admin", "admin"]} element={Export} />} path="/export/*" />
            <Route element={<RoleProtectedRoute allowedRoles={["super_admin"]} element={Cashout} />} path="/cashout/*" />
            <Route element={<RoleProtectedRoute allowedRoles={["super_admin", "admin", "user"]} element={People} />} path="/people/*" />
            <Route element={<RoleProtectedRoute allowedRoles={["super_admin", "admin", "user"]} element={Invoice} />} path="/invoice/*" />
            {/* Quote routes removed */}
            <Route element={<RoleProtectedRoute allowedRoles={["super_admin", "admin"]} element={Bank} />} path="/bank/*" />
            <Route element={<RoleProtectedRoute allowedRoles={["super_admin", "admin", "user"]} element={BankDuplicates} />} path="/bank/duplicates/*" />
            <Route element={<RoleProtectedRoute allowedRoles={["super_admin", "admin", "user"]} element={Malt} />} path="/malt/*" />
            <Route element={<RoleProtectedRoute allowedRoles={["super_admin", "admin", "user"]} element={Organisation} />} path="/organisation/*" />
            <Route element={<RoleProtectedRoute allowedRoles={["super_admin", "admin"]} element={BusinessUnit} />} path="/business_unit/*" />
            <Route element={<RoleProtectedRoute allowedRoles={["super_admin", "admin", "user"]} element={Management} />} path="/management/*" />
            <Route element={<RoleProtectedRoute allowedRoles={["super_admin", "admin"]} element={Performance} />} path="/performance/*" />
            <Route element={<RoleProtectedRoute allowedRoles={["super_admin", "admin", "user"]} element={LegalDocument} />} path="/legal_document/*" />
            <Route element={<RoleProtectedRoute allowedRoles={["super_admin", "admin", "user"]} element={Device} />} path="/device/*" />
            <Route element={<RoleProtectedRoute allowedRoles={["super_admin", "admin"]} element={Piloting} />} path="/piloting/*" />
            <Route element={<RoleProtectedRoute allowedRoles={["super_admin", "admin"]} element={Tace} />} path="/tace/*" />
            <Route element={<RoleProtectedRoute allowedRoles={["super_admin", "admin", "user"]} element={Resources} />} path="/resources/*" />
            <Route element={<RoleProtectedRoute allowedRoles={["super_admin", "admin", "user"]} element={Documentation} />} path="/documentation/*" />
          </Route>

          {/* Public Modules */}
          <Route path="/selecole/course/:id" element={<CourseView />} />
          <Route path="/selecole/learn/:id" element={<LearnWorkshop />} />

          {/* Restricted Modules */}
          <Route element={<ModuleProtectedLayout />}>
            <Route element={<RoleProtectedRoute allowedRoles={["super_admin", "admin", "user"]} element={SelecoleApp} />} path="/selecole/*" />
            <Route element={<RoleProtectedRoute allowedRoles={["super_admin", "admin", "user"]} element={GitheoApp} />} path="/githeo/*" />
            <Route element={<RoleProtectedRoute allowedRoles={["super_admin", "admin", "user"]} element={RfpApp} />} path="/rfp/*" />
          </Route>
        </Routes>
      </Chat>
    </BrowserRouter>
  );
}

const OpenLayout = () => {
  return (
    <div className="w-screen h-screen flex justify-center bg-white">
      <main className="flex-1 overflow-x-auto bg-[#FBFBF9]">
        <Outlet />
      </main>
    </div>
  );
};

const ProtectedLayout = () => {
  const { sideBarOpen } = useStore();
  const dispatch = useDispatch();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const user = useSelector((state) => state.Auth.user);
  const redirect = encodeURIComponent(window.location.href.replace(window.location.origin, ""));

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    try {
      const res = await api.get("/user/signin_token");
      if (!res.ok || !res.user) return setLoading(false);
      if (res.token) api.setToken(res.token);
      dispatch(setUser(res.user));
      dispatch(setApp("accounting"));

      if (res.user.organisation_id) {
        const { data, ok } = await api.get(`/organisation/${res.user.organisation_id}`);
        if (!ok) throw new Error("Error fetching organisation");
        dispatch(setOrganisation(data));
      }

      // Set business unit from user
      if (res.user.business_unit_id) {
        dispatch(setBusinessUnit({ _id: res.user.business_unit_id, name: res.user.business_unit_name }));
      }
    } catch (e) {
      console.log(e);
    }
    setLoading(false);
  }

  if (loading) return <Loader />;
  if (!user) return <Navigate to={`/auth?redirect=${redirect}`} replace />;

  return (
    <div className="w-screen relative bg-white">
      <SideBar />
      <div className={`${sideBarOpen ? "md:ml-64" : "ml-12"} flex flex-col ease-in-out duration-300`}>
        <Header />
        <main className="flex-1 overflow-x-auto bg-[#FBFBF9]">
          <div className="h-[calc(100vh-61px)] overflow-y-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

const ModuleProtectedLayout = () => {
  const { sideBarOpen } = useStore();
  const dispatch = useDispatch();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const user = useSelector((state) => state.Auth.user);
  const redirect = encodeURIComponent(window.location.href.replace(window.location.origin, ""));

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    try {
      const res = await api.get("/user/signin_token");
      if (!res.ok || !res.user) return setLoading(false);
      if (res.token) api.setToken(res.token);
      dispatch(setUser(res.user));
      dispatch(setApp("accounting"));

      if (res.user.organisation_id) {
        const { data, ok } = await api.get(`/organisation/${res.user.organisation_id}`);
        if (!ok) throw new Error("Error fetching organisation");
        dispatch(setOrganisation(data));
      }
    } catch (e) {
      console.log(e);
    }
    setLoading(false);
  }

  if (loading) return <Loader />;
  if (!user) return <Navigate to={`/auth?redirect=${redirect}`} replace />;

  return <Outlet />;
};

const RoleProtectedRoute = ({ element: Element, allowedRoles, ...rest }) => {
  const user = useSelector((state) => state.Auth.user);
  if (!user) return <Navigate to="/auth" replace />;
  if (!allowedRoles.includes(user.role)) {
    if (user.role === "client") return <Navigate to="/project" replace />;
    return <div>Forbidden</div>;
  }
  return <Element />;
};

